import React, { useState, useMemo } from "react";
import { Button } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useBrand } from "@webuild-ai/react-auth";

import { Artefact } from "@/types/artefacts";
import { ARTEFACT_CONFIG } from "@/config/artefacts";
import {
  parseTableContent,
  sortTableData,
  SortConfig,
  getColumnDataTypes,
} from "@/utils/tableUtils";

// Default font when no brand font is specified (matches tailwind.config)
const DEFAULT_FONT_FAMILY = '"Poppins", sans-serif';

interface TableArtefactViewerProps {
  artefact: Artefact;
}

interface SortableTableProps {
  content: string;
  fontFamily: string;
}

const SortableTable: React.FC<SortableTableProps> = ({ content, fontFamily }) => {
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);

  const tableData = useMemo(() => parseTableContent(content), [content]);

  const sortedData = useMemo(() => {
    if (!tableData || !sortConfig) {
      return tableData?.rows || [];
    }

    return sortTableData(tableData.rows, sortConfig.column, sortConfig.direction);
  }, [tableData, sortConfig]);

  const handleSort = (column: string) => {
    setSortConfig((current) => {
      if (current?.column === column) {
        // Toggle direction if same column
        return {
          column,
          direction: current.direction === "asc" ? "desc" : "asc",
        };
      }

      // New column, start with ascending
      return { column, direction: "asc" };
    });
  };

  const getSortIcon = (column: string) => {
    if (!sortConfig || sortConfig.column !== column) {
      return null;
    }

    return sortConfig.direction === "asc" ? (
      <Icon className="w-4 h-4 ml-1 inline" icon="gravity-ui:arrow-up" />
    ) : (
      <Icon className="w-4 h-4 ml-1 inline" icon="gravity-ui:arrow-down" />
    );
  };

  if (!tableData) {
    // Fallback: display as formatted text
    return (
      <div className="bg-white dark:bg-default-50 rounded border border-default-200 dark:border-default-700 overflow-hidden">
        <pre className="text-sm font-mono text-default-800 dark:text-white whitespace-pre-wrap break-words overflow-auto max-h-96 p-4">
          <code>{content}</code>
        </pre>
      </div>
    );
  }

  const { headers } = tableData;
  const displayRows = sortedData.slice(0, ARTEFACT_CONFIG.detection.maxTableDisplayRows);
  const columnDataTypes = getColumnDataTypes(headers, tableData.rows);

  return (
    <div
      className="bg-white dark:bg-default-50 rounded border border-default-200 dark:border-default-700 overflow-hidden"
      style={{ fontFamily }}
    >
      <div className="overflow-x-auto max-h-96">
        <table className="min-w-full">
          <thead className="bg-default-100 dark:bg-default-100/50">
            <tr>
              {headers.map((header, index) => (
                <th
                  key={index}
                  className="border-b border-r last:border-r-0 border-default-200 dark:border-default-700"
                >
                  <Button
                    className="w-full justify-start px-3 py-2 text-left text-xs font-medium text-default-700 dark:text-white uppercase tracking-wider hover:bg-default-200 dark:hover:bg-default-200/30"
                    variant="light"
                    onClick={() => handleSort(header)}
                  >
                    <span className="flex items-center">
                      {header}
                      <span className="ml-1 text-xs text-default-500 dark:text-default-400">
                        ({columnDataTypes[header]})
                      </span>
                      {getSortIcon(header)}
                    </span>
                  </Button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-default-200 dark:divide-default-700">
            {displayRows.map((row: any, rowIndex: number) => (
              <tr key={rowIndex} className="hover:bg-default-50 dark:hover:bg-default-100/30">
                {headers.map((header, colIndex) => (
                  <td
                    key={colIndex}
                    className="px-3 py-2 text-sm text-default-800 dark:text-white border-b border-r last:border-r-0 border-default-200 dark:border-default-700 max-w-xs truncate"
                    title={String(row[header] || "")}
                  >
                    {String(row[header] || "")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {tableData.totalRows > ARTEFACT_CONFIG.detection.maxTableDisplayRows && (
          <div className="p-3 text-xs text-default-600 dark:text-default-300 bg-default-100 dark:bg-default-100/50 border-t border-default-200 dark:border-default-700">
            Showing first {ARTEFACT_CONFIG.detection.maxTableDisplayRows} rows of{" "}
            {tableData.totalRows} total rows
            {sortConfig && (
              <span className="ml-2 text-primary dark:text-primary-300">
                (sorted by {sortConfig.column} {sortConfig.direction})
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const TableArtefactViewer: React.FC<TableArtefactViewerProps> = ({ artefact }) => {
  const { brand } = useBrand();
  const fontFamily = brand.fontFamily || DEFAULT_FONT_FAMILY;

  return (
    <div data-artefact-id={artefact.id}>
      <SortableTable content={artefact.content} fontFamily={fontFamily} />
    </div>
  );
};
